<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import { DTextField, DBtn, EyeIcon, EyeOffIcon } from 'front-library'

/**
 * store y router
 */
const router = useRouter()
const store = useUserStore()
const { t } = useI18n()

/**
 * Datos componente
 */
const show = ref(true)
const loading = ref(false)
const wrongData = ref('')

/**
 * Datos formulario
 */
const dataLogin = ref({
  userEmail: '',
  userPassword: ''
})

/**
 * Esquema de validacion campos obligatorios
 */
const validationSchema = object().shape({
  userEmail: string()
    .matches(
      /^[a-zA-Z0-9.-_]+@[a-zA-Z]+\.[a-zA-Z]{2,3}$/g,
      t('auth.login.form.valid.email')
    )
    .required(t('auth.login.form.valid.required')),
  userPassword: string().required(t('auth.login.form.valid.required'))
})

/**
 * Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario
 */
const { handleSubmit, errors } = useForm({
  initialValues: dataLogin,
  validationSchema
})

/**
 * Definicion de  campos del formulario
 */
const { value: userEmail } = useField<string>('userEmail')
const { value: userPassword } = useField<string>('userPassword')

/*
 * Metodo que valida los campos obligatorios del formulario
 */
const onSubmit = handleSubmit((values, actions) => {
  void login(values)
})

/**
 * Metodo para iniciar sesión
 */
const login = async (values: any) => {
  values.userNewPassword = ''
  loading.value = true
  try {
    await store.login(values).then((result: any) => {
      switch (result) {
        case true:
          wrongData.value = ''
          void router.push({ name: 'Dashboard' })
          break
        case false:
          loading.value = false
          wrongData.value = t('auth.login.valid.invalidLogin')
          break
        default:
          switch (result.name) {
            case 'AxiosError':
              loading.value = false
              wrongData.value = t('auth.login.valid.axiosError')
              break
            case 'NotAuthorizedException':
              loading.value = false
              wrongData.value = result.message
              break
            case 'ForceChangePassword':
              wrongData.value = ''
              store.emailForgot = values.userEmail
              store.pass = values.userPassword
              void router.push({ name: 'ChangeTemporaryPassword' })
              break
            default:
              loading.value = false
              wrongData.value = result.message
              break
          }
          break
      }
    })
  } catch (error: any) {
    loading.value = false
    wrongData.value = error.message
  }
}

onMounted(() => {
  wrongData.value = store.tokenInvalid ? t('auth.login.valid.tokenInvalid') : ''
  store.tokenInvalid = false
})
</script>
<template>
  <h2 class="text-3xl xl:text-4xl font-bold" v-html="t('auth.login.title')" />
  <p
    class="py-3 text-gray-400 text-md md:text-lg leading-6"
    v-html="t('auth.login.description')" />
  <div
    v-if="wrongData"
    class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
    role="alert">
    <p class="text-sm font-bold">{{ wrongData }}</p>
  </div>
  <form class="grid" @submit.prevent="onSubmit">
    <div class="grid mb-3">
      <DTextField
        :label="t('auth.login.form.email')"
        type="email"
        :error="errors.userEmail"
        :placeholder="t('auth.login.form.placeholder.email')"
        :required="true"
        v-model="userEmail" />
    </div>
    <div class="grid relative mb-3">
      <DTextField
        :label="t('auth.login.form.password')"
        :type="show ? 'password' : 'text'"
        :error="errors.userPassword"
        :placeholder="t('auth.login.form.placeholder.password')"
        :required="true"
        v-model="userPassword" />
      <div class="absolute right-3 top-8 z-50">
        <EyeIcon
          class="text-gray-600"
          @click="show = !show"
          :class="{ hidden: !show, block: show }" />
        <EyeOffIcon
          class="text-gray-600"
          @click="show = !show"
          :class="{ block: !show, hidden: show }" />
      </div>
    </div>
    <div class="flex justify-between pt-4 pb-6">
      <p v-html="t('auth.login.forgetPass')" />
      <router-link
        :to="{ name: 'ForgotPassword' }"
        class="text-blue-600 hover:text-blue-700 font-bold"
        v-html="t('auth.login.recoverPass')" />
    </div>
    <DBtn
      class="font-semibold py-3 text-base"
      :loading="loading"
      :color="
        errors.userEmail == null && errors.userPassword == null
          ? 'blue'
          : 'disabled'
      "
      :disabled="
        errors.userEmail == null && errors.userPassword == null ? false : true
      ">
      {{ t('auth.login.logIn') }}
    </DBtn>
  </form>
</template>
